import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Users,
  TrendingUp,
  CheckSquare,
  Briefcase,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useDateTime() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ---------------------------------------------------------------------------
// Company KPI card
// ---------------------------------------------------------------------------

function KpiCard({
  label,
  value,
  icon: Icon,
  accentColor,
}: {
  label: string;
  value: number | null;
  icon: React.ElementType;
  accentColor: string; // tailwind text colour class
}) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {label}
            </p>
            <p
              className={`mt-2 text-4xl font-bold tabular-nums ${accentColor}`}
            >
              {value === null ? "—" : value.toLocaleString()}
            </p>
          </div>
          <div
            className={`rounded-xl p-3 ${accentColor.replace("text-", "bg-").replace(/(-\d+)$/, "$1/10")}`}
          >
            <Icon className={`h-6 w-6 ${accentColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Department card
// ---------------------------------------------------------------------------

interface DepartmentStat {
  label: string;
  value: number | null;
}

interface Department {
  name: string;
  description: string;
  emoji: string;
  path: string | null;
  stats: DepartmentStat[];
  borderColor: string; // tailwind border colour class
  accentBg: string; // tailwind bg colour class for emoji circle
  comingSoon?: boolean;
}

function DepartmentCard({ dept }: { dept: Department }) {
  const navigate = useNavigate();
  const clickable = !!dept.path;

  return (
    <Card
      className={`border-l-4 ${dept.borderColor} bg-card transition-colors ${
        clickable ? "cursor-pointer hover:bg-accent" : "opacity-80"
      }`}
      onClick={() => clickable && navigate(dept.path!)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Left: icon + text */}
          <div className="flex items-start gap-4 min-w-0">
            <div
              className={`flex-shrink-0 flex items-center justify-center rounded-xl w-12 h-12 text-2xl ${dept.accentBg}`}
            >
              {dept.emoji}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold">{dept.name}</h3>
                {dept.comingSoon && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] uppercase tracking-wider"
                  >
                    Coming Soon
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {dept.description}
              </p>

              {/* Stats row */}
              {dept.stats.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {dept.stats.map((s) => (
                    <div
                      key={s.label}
                      className="rounded-md bg-secondary px-3 py-1.5"
                    >
                      <span className="block text-xs text-muted-foreground">
                        {s.label}
                      </span>
                      <span className="block text-sm font-semibold tabular-nums">
                        {s.value === null ? "—" : s.value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: arrow for clickable */}
          {clickable && (
            <ArrowRight className="h-5 w-5 flex-shrink-0 text-muted-foreground mt-1" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent Activity Feed
// ---------------------------------------------------------------------------

function RecentActivityFeed() {
  const [items, setItems] = useState<
    Array<{ id: string | number; label: string; time: string; type: string }>
  >([]);

  useEffect(() => {
    const load = async () => {
      const results: Array<{
        id: string | number;
        label: string;
        time: string;
        type: string;
      }> = [];

      const { data: recentContacts } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, created_at")
        .order("created_at", { ascending: false })
        .limit(4);

      if (recentContacts) {
        recentContacts.forEach((c: any) => {
          results.push({
            id: `contact-${c.id}`,
            label: `Contact added: ${c.first_name} ${c.last_name}`,
            time: c.created_at,
            type: "contact",
          });
        });
      }

      const { data: recentScores } = await supabase
        .from("contact_scores")
        .select(
          "id, total_score, tier, scored_at, contacts(first_name, last_name)",
        )
        .order("scored_at", { ascending: false })
        .limit(3);

      if (recentScores) {
        recentScores.forEach((s: any) => {
          results.push({
            id: `score-${s.id}`,
            label: `Scored ${s.contacts?.first_name ?? ""} ${s.contacts?.last_name ?? ""} — ${s.tier ?? ""} (${s.total_score ?? 0})`,
            time: s.scored_at,
            type: "score",
          });
        });
      }

      const { data: recentTasks } = await supabase
        .from("tasks")
        .select("id, type, done_date, updated_at")
        .not("done_date", "is", null)
        .order("done_date", { ascending: false })
        .limit(3);

      if (recentTasks) {
        recentTasks.forEach((t: any) => {
          results.push({
            id: `task-${t.id}`,
            label: `Task completed: ${t.type ?? "task"}`,
            time: t.done_date,
            type: "task",
          });
        });
      }

      const { data: recentDeals } = await supabase
        .from("deals")
        .select("id, name, updated_at")
        .order("updated_at", { ascending: false })
        .limit(3);

      if (recentDeals) {
        recentDeals.forEach((d: any) => {
          results.push({
            id: `deal-${d.id}`,
            label: `Deal updated: ${d.name ?? "(unnamed)"}`,
            time: d.updated_at,
            type: "deal",
          });
        });
      }

      results.sort(
        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
      );

      setItems(results.slice(0, 10));
    };

    load();
  }, []);

  const typeBadgeClass: Record<string, string> = {
    contact: "bg-blue-500/10 text-blue-400",
    score: "bg-green-500/10 text-green-400",
    task: "bg-amber-500/10 text-amber-400",
    deal: "bg-purple-500/10 text-purple-400",
  };

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No recent activity yet.</p>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${typeBadgeClass[item.type] ?? ""}`}
            >
              {item.type}
            </span>
            <span className="truncate text-foreground">{item.label}</span>
          </div>
          <span className="ml-4 flex-shrink-0 text-xs text-muted-foreground">
            {new Date(item.time).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface CompanyKpis {
  teamMembers: number | null;
  activeLeads: number | null;
  openDeals: number | null;
  tasksDueThisWeek: number | null;
}

export function CommandCenter() {
  const now = useDateTime();
  const [kpis, setKpis] = useState<CompanyKpis>({
    teamMembers: null,
    activeLeads: null,
    openDeals: null,
    tasksDueThisWeek: null,
  });
  const [kpiError, setKpiError] = useState<string | null>(null);

  // Marketing & Sales dept stats
  const [msStats, setMsStats] = useState<{
    angelCount: number | null;
    totalContacts: number | null;
    openTasks: number | null;
  }>({ angelCount: null, totalContacts: null, openTasks: null });

  useEffect(() => {
    const load = async () => {
      setKpiError(null);
      try {
        const now = new Date();
        const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        const [
          { count: teamMembers, error: e1 },
          { count: activeLeads, error: e2 },
          { count: openDeals, error: e3 },
          { count: tasksDueThisWeek, error: e4 },
          { count: angelCount, error: e5 },
          { count: totalContacts, error: e6 },
          { count: openTasks, error: e7 },
        ] = await Promise.all([
          // Company KPIs
          supabase
            .from("sales")
            .select("*", { count: "exact", head: true })
            .eq("disabled", false),
          supabase
            .from("contacts")
            .select("*", { count: "exact", head: true })
            .in("contact_status", [
              "qualified",
              "contacted",
              "in_conversation",
            ]),
          supabase
            .from("deals")
            .select("*", { count: "exact", head: true })
            .is("archived_at", null),
          supabase
            .from("tasks")
            .select("*", { count: "exact", head: true })
            .gte("due_date", now.toISOString())
            .lte("due_date", weekLater.toISOString())
            .is("done_date", null),
          // M&S dept stats
          supabase
            .from("contacts")
            .select("*", { count: "exact", head: true })
            .eq("current_tier", "Angel Target"),
          supabase.from("contacts").select("*", { count: "exact", head: true }),
          supabase
            .from("tasks")
            .select("*", { count: "exact", head: true })
            .is("done_date", null),
        ]);

        const firstError = e1 || e2 || e3 || e4 || e5 || e6 || e7;
        if (firstError) {
          console.error("KPI load error:", firstError);
          setKpiError(firstError.message);
        }

        setKpis({
          teamMembers: teamMembers ?? null,
          activeLeads: activeLeads ?? null,
          openDeals: openDeals ?? null,
          tasksDueThisWeek: tasksDueThisWeek ?? null,
        });

        setMsStats({
          angelCount: angelCount ?? null,
          totalContacts: totalContacts ?? null,
          openTasks: openTasks ?? null,
        });
      } catch (err) {
        console.error("Unexpected KPI error:", err);
        setKpiError("Failed to load metrics");
      }
    };

    load();
  }, []);

  const departments: Department[] = [
    {
      name: "Marketing & Sales",
      description: "Lead scoring, contact management, outreach, deals pipeline",
      emoji: "📊",
      path: "/contacts",
      stats: [
        { label: "Angel Targets", value: msStats.angelCount },
        { label: "Contacts", value: msStats.totalContacts },
        { label: "Open Tasks", value: msStats.openTasks },
      ],
      borderColor: "border-blue-500",
      accentBg: "bg-blue-500/10",
    },
    {
      name: "Accounting & Finance",
      description: "Invoicing, expenses, financial reporting",
      emoji: "💰",
      path: null,
      stats: [],
      borderColor: "border-green-500",
      accentBg: "bg-green-500/10",
      comingSoon: true,
    },
    {
      name: "Operations",
      description: "Project delivery, resource planning, time tracking",
      emoji: "⚙️",
      path: null,
      stats: [],
      borderColor: "border-orange-500",
      accentBg: "bg-orange-500/10",
      comingSoon: true,
    },
    {
      name: "Strategy & Innovation",
      description: "Research, frameworks, methodology library",
      emoji: "🧠",
      path: null,
      stats: [],
      borderColor: "border-purple-500",
      accentBg: "bg-purple-500/10",
      comingSoon: true,
    },
  ];

  const dateStr = now.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div className="min-h-screen bg-background p-6 max-w-7xl mx-auto">
      {/* ------------------------------------------------------------------ */}
      {/* Header */}
      {/* ------------------------------------------------------------------ */}
      <div className="mb-10 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">
            MYLES Command Center
          </h1>
          <p className="mt-1 text-base font-medium text-muted-foreground uppercase tracking-widest">
            MYLES STRATEGY AND INNOVATION
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{dateStr}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-mono text-3xl font-bold tabular-nums">{timeStr}</p>
          <div className="mt-1 flex justify-end">
            <Badge
              variant="outline"
              className="text-xs text-green-500 border-green-500/40"
            >
              ● Live
            </Badge>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Company KPI Row */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-10">
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Company Overview
        </h2>
        {kpiError && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {kpiError}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KpiCard
            label="Team Members"
            value={kpis.teamMembers}
            icon={Users}
            accentColor="text-blue-400"
          />
          <KpiCard
            label="Active Leads"
            value={kpis.activeLeads}
            icon={TrendingUp}
            accentColor="text-green-400"
          />
          <KpiCard
            label="Open Deals"
            value={kpis.openDeals}
            icon={Briefcase}
            accentColor="text-purple-400"
          />
          <KpiCard
            label="Tasks Due This Week"
            value={kpis.tasksDueThisWeek}
            icon={CheckSquare}
            accentColor="text-amber-400"
          />
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Department Cards */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-10">
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Departments
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {departments.map((dept) => (
            <DepartmentCard key={dept.name} dept={dept} />
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Recent Activity Feed */}
      {/* ------------------------------------------------------------------ */}
      <section>
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Recent Activity
        </h2>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Latest Events</CardTitle>
          </CardHeader>
          <CardContent>
            <RecentActivityFeed />
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
